import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { IconStatCardList, type IconStatCardLayout } from './IconStatCard';
import { SectionHeader } from '../SectionHeader';

interface QuickActionsSectionProps {
  openTicketsCount: number;
  /** Newsfeed threads posted since the resident last opened the board. */
  newsfeedNewCount: number;
  unreadMessagesCount: number;
  /** Open surveys the resident hasn't responded to yet. */
  surveysCount: number;
  onMaintenance: () => void;
  onNewsfeed: () => void;
  onMessages: () => void;
  onSurveys: () => void;
  /**
   * `row` (default) is the mobile edge-bleeding horizontal scroller; `grid`
   * wraps the same cards two-up inside a desktop column.
   */
  layout?: IconStatCardLayout;
  /** Page padding the `row` layout bleeds past. Defaults to the 24pt page gutter. */
  horizontalPadding?: number;
  /** Extra per-card sizing, applied after the layout's own. */
  itemStyle?: StyleProp<ViewStyle>;
  /**
   * Tightens the section header's vertical padding — for the no-scroll home,
   * where header whitespace is height the planner does not get. Off by default.
   */
  denseHeader?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const QuickActionsSection: React.FC<QuickActionsSectionProps> = React.memo(({
  openTicketsCount,
  newsfeedNewCount,
  unreadMessagesCount,
  surveysCount,
  onMaintenance,
  onNewsfeed,
  onMessages,
  onSurveys,
  layout,
  horizontalPadding,
  itemStyle,
  denseHeader = false,
  style,
}) => {
  const { colors } = useThemeColors();

  const items = [
    {
      key: 'maintenance',
      label: 'Maintenance',
      caption: `${openTicketsCount} open`,
      iconBg: colors.accent.orange,
      Icon: RESIDENTS_SVG_COMPONENTS.WRENCH,
      iconColor: PRIMITIVE_COLORS.warningOrange,
      onPress: onMaintenance,
    },
    {
      key: 'newsfeed',
      label: 'Newsfeed',
      caption: `${newsfeedNewCount} new`,
      iconBg: colors.accent.purple,
      Icon: RESIDENTS_SVG_COMPONENTS.BOARD,
      iconColor: PRIMITIVE_COLORS.resioPurple,
      onPress: onNewsfeed,
    },
    {
      key: 'messages',
      label: 'Messages',
      caption: `${unreadMessagesCount} unread`,
      iconBg: colors.accent.blueBg,
      Icon: RESIDENTS_SVG_COMPONENTS.CHAT_ICON,
      iconColor: PRIMITIVE_COLORS.resioBlue,
      onPress: onMessages,
    },
    {
      key: 'surveys',
      label: 'Surveys',
      caption: `${surveysCount} to answer`,
      iconBg: colors.accent.greenLight,
      Icon: RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE,
      iconColor: PRIMITIVE_COLORS.successGreen,
      onPress: onSurveys,
    },
  ];

  return (
    <View style={style}>
      <SectionHeader title="Quick Actions" dense={denseHeader} />
      <IconStatCardList
        items={items}
        layout={layout}
        horizontalPadding={horizontalPadding}
        itemStyle={itemStyle}
        showCountBadge
      />
    </View>
  );
});

QuickActionsSection.displayName = 'QuickActionsSection';
